import {EditOutlined} from '@ant-design/icons';
import {Button, DatePicker, Form, Input, Modal, Select, TimePicker, message} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, {useEffect} from 'react';

import {RQAppointmentUpdateAPI, RqUserLookupAllRole} from '../../../../ReactQuery/apis';

const {TextArea} = Input;

const toDayjsTime = time => {
  if (!time) {return null;}

  if (typeof time === 'string') {
    const [hour, minute] = time.split(':');

    return dayjs().hour(Number(hour)).minute(Number(minute)).second(0);
  }

  return dayjs().hour(time.hour || 0).minute(time.minute || 0).second(0);
};

// ─── Edit Appointment Modal ─────────────────────────────────────────────────
const EditAppoinment = ({open, onClose, appointment, token, onUpdated}) => {
  const [form] = Form.useForm();

  const {data: DOCTOR = [], isFetching: doctorsLoading} = RqUserLookupAllRole('DOCTOR', token, {
    enabled: open,
  });
  
  const {data: THERAPIST = [], isFetching: therapistsLoading} = RqUserLookupAllRole('THERAPIST', token, {
    enabled: open,
  });

  const DOCTOR_OPTIONS = [
    ...DOCTOR.map(d => ({...d, role: 'Doctor'})),
    ...THERAPIST.map(t => ({...t, role: 'Therapist'})),
  ];

  const {mutate: updateAppointment, isLoading: submitting} = RQAppointmentUpdateAPI({
    onSuccess: () => {
      message.success('Appointment updated');
      onUpdated?.();
      onClose();
    },
    onError: err => {
      message.error(err?.response?.data?.message || 'Failed to update appointment');
    },
  });

  useEffect(() => {
    if (appointment) {
      form.setFieldsValue({
        doctorId: appointment.doctorId,
        appointmentDate: appointment.appointmentDate ? dayjs(appointment.appointmentDate) : null,
        appointmentTime: toDayjsTime(appointment.appointmentTime),
        reason: appointment.reason,
      });
    }
  }, [appointment, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = values => {
    const time = values.appointmentTime;

    const formData = {
      doctorId: values.doctorId,
      appointmentDate: values.appointmentDate.format('YYYY-MM-DD'),
      appointmentTime: {
        hour: time.hour(),
        minute: time.minute(),
        second: 0,
        nano: 0,
      },
      reason: values.reason,
    };

    updateAppointment({
      id: appointment.id,
      formData,
      token,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      width={520}
      title={(
        <div className={'flex items-center gap-2'}>
          <div
            className={'w-9 h-9 rounded-lg flex items-center justify-center text-white'}
            style={{background: 'linear-gradient(135deg, #b69c66, #b69c66)'}}
          >
            <EditOutlined />
          </div>
          <span className={'text-primary-700 font-bold text-base'}>Edit Appointment</span>
        </div>
      )}
    >
      {appointment && (
        <div className={'bg-primary-50 rounded-lg px-4 py-3 mb-4 text-sm'}>
          <p className={'m-0 text-primary-700'}>
            Patient: <span className={'font-semibold'}>{appointment.patientName}</span>
          </p>
        </div>
      )}

      <Form form={form} layout={'vertical'} onFinish={handleFinish}>
       
        <Form.Item
          label={<span className={'font-medium text-primary-700'}>Doctor</span>}
          name={'doctorId'}
          rules={[{required: true, message: 'Please select a doctor'}]}
        >
          <Select
            showSearch
            placeholder={'Select doctor'}
            loading={doctorsLoading || therapistsLoading}
            optionFilterProp={'label'}
            options={DOCTOR_OPTIONS.map(d => ({
              value: d.id,
              label: `${d.fullName} — ${d.role}`,
            }))}
          />
        </Form.Item>

        <div className={'grid grid-cols-2 gap-4'}>
          <Form.Item
            label={<span className={'font-medium text-primary-700'}>Date</span>}
            name={'appointmentDate'}
            rules={[{required: true, message: 'Please pick a date'}]}
          >
            <DatePicker className={'w-full'} format={'YYYY-MM-DD'} />
          </Form.Item>

          <Form.Item
            label={<span className={'font-medium text-primary-700'}>Time</span>}
            name={'appointmentTime'}
            rules={[{required: true, message: 'Please pick a time'}]}
          >
            <TimePicker className={'w-full'} format={'HH:mm'} minuteStep={5} />
          </Form.Item>
        </div>

        <Form.Item
          label={<span className={'font-medium text-primary-700'}>Reason</span>}
          name={'reason'}
        >
          <TextArea rows={3} placeholder={'Reason for visit / update'} />
        </Form.Item>

        <div className={'flex justify-end gap-2 mt-6'}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type={'primary'}
            htmlType={'submit'}
            loading={submitting}
            className={'bg-primary-700 hover:!bg-primary-800 border-none'}
          >
            Save Changes
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

EditAppoinment.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  appointment: PropTypes.object,
  token: PropTypes.string,
  onUpdated: PropTypes.func,
};

export default EditAppoinment;
